import PropTypes from "prop-types";
import { useMemo, useState } from "react";
import { FilterSelect } from "@/components/common/FilterSelect";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import {
  ACTIVITY_DATE_OPTIONS,
  buildActivityEventTypeOptions,
  filterCampaignActivity,
  formatActivityEventDescription,
  formatActivityEventLabel,
  getActivityCategoryMeta,
  groupActivityByDate,
} from "@/lib/campaignActivity";

function ActivityEventRow({ event }) {
  const category = getActivityCategoryMeta(event.event_type);
  const title = formatActivityEventLabel(event.event_type);
  const description = formatActivityEventDescription(event);
  const timestamp = event.created_at
    ? new Date(event.created_at).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "medium",
    })
    : "—";

  return (
    <li
      className="px-3 py-3 sm:px-4 flex flex-col gap-1.5 sm:flex-row sm:items-start sm:justify-between"
      data-testid={`activity-event-${event.id}`}
    >
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={cn(
              "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ring-1 ring-inset",
              category.className,
            )}
          >
            {category.label}
          </span>
          <span className="font-medium text-[13px] text-slate-900">{title}</span>
        </div>
        {description ? (
          <p className="mt-1 text-[12px] text-slate-600 leading-relaxed">{description}</p>
        ) : null}
        {event.enrollment_id ? (
          <p className="mt-1 text-[11px] text-slate-400">
            Prospect {String(event.enrollment_id).slice(0, 8)}…
          </p>
        ) : null}
      </div>
      <time
        className="text-[11.5px] text-slate-400 shrink-0 sm:text-right sm:max-w-[11rem]"
        dateTime={event.created_at || undefined}
      >
        {timestamp}
      </time>
    </li>
  );
}

ActivityEventRow.propTypes = {
  event: PropTypes.shape({
    id: PropTypes.string,
    event_type: PropTypes.string,
    created_at: PropTypes.string,
    enrollment_id: PropTypes.string,
  }).isRequired,
};

function ActivityFilterBar({
  eventTypeFilter,
  setEventTypeFilter,
  eventTypeOptions,
  dateFilter,
  onDateFilterChange,
  hasActiveFilters,
}) {
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-slate-50/70 p-2.5">
      <FilterSelect
        variant="field"
        value={eventTypeFilter}
        onChange={setEventTypeFilter}
        options={eventTypeOptions}
        className="w-auto min-w-[148px]"
        triggerClassName="h-8 px-2.5 text-[12px]"
        data-testid="activity-filter-event-type"
        aria-label="Filter by event type"
      />
      <FilterSelect
        variant="field"
        value={dateFilter}
        onChange={onDateFilterChange}
        options={ACTIVITY_DATE_OPTIONS}
        className="w-auto min-w-[128px]"
        triggerClassName="h-8 px-2.5 text-[12px]"
        data-testid="activity-filter-date"
        aria-label="Filter by date"
      />
      {hasActiveFilters ? (
        <button
          type="button"
          className="text-[12px] font-medium text-indigo-600 hover:text-indigo-700 px-1"
          onClick={() => {
            setEventTypeFilter("all");
            onDateFilterChange("all");
          }}
          data-testid="activity-filter-clear"
        >
          Clear filters
        </button>
      ) : null}
    </div>
  );
}

ActivityFilterBar.propTypes = {
  eventTypeFilter: PropTypes.string.isRequired,
  setEventTypeFilter: PropTypes.func.isRequired,
  eventTypeOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  dateFilter: PropTypes.string.isRequired,
  onDateFilterChange: PropTypes.func.isRequired,
  hasActiveFilters: PropTypes.bool.isRequired,
};

export function CampaignActivityPanel({
  activity,
  activityLoading,
  activityTotal,
  dateFilter,
  onDateFilterChange,
}) {
  const [eventTypeFilter, setEventTypeFilter] = useState("all");

  const eventTypeOptions = useMemo(
    () => buildActivityEventTypeOptions(activity),
    [activity],
  );

  const filtered = useMemo(
    () => filterCampaignActivity(activity, {
      eventType: eventTypeFilter,
      dateRange: dateFilter,
    }),
    [activity, dateFilter, eventTypeFilter],
  );

  const grouped = useMemo(() => groupActivityByDate(filtered), [filtered]);

  const hasActiveFilters = eventTypeFilter !== "all" || dateFilter !== "all";
  const rangeLabel = ACTIVITY_DATE_OPTIONS.find((o) => o.value === dateFilter)?.label
    || "All time";
  const notFetched = Math.max(0, (activityTotal ?? activity.length) - activity.length);

  return (
    <div className="space-y-3" data-testid="campaign-activity-panel">
      <ActivityFilterBar
        eventTypeFilter={eventTypeFilter}
        setEventTypeFilter={setEventTypeFilter}
        eventTypeOptions={eventTypeOptions}
        dateFilter={dateFilter}
        onDateFilterChange={onDateFilterChange}
        hasActiveFilters={hasActiveFilters}
      />

      {activityLoading ? (
        <Skeleton className="h-24 w-full" data-testid="activity-loading" />
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-2 text-[12px] text-slate-500">
            <span data-testid="activity-filter-count">
              Showing {filtered.length} of {activity.length} events · {rangeLabel}
            </span>
            {notFetched > 0 ? (
              <span className="text-slate-400" data-testid="activity-truncated">
                Newest {activity.length} shown; {notFetched} older event
                {notFetched === 1 ? "" : "s"} in this range not loaded
              </span>
            ) : null}
          </div>

          {activity.length === 0 ? (
            <div
              className="rounded-lg border border-dashed border-slate-200 bg-slate-50/50 p-6 text-center text-[13px] text-slate-500"
              data-testid="activity-empty"
            >
              {dateFilter === "all"
                ? "No activity yet. Publish, enroll, or simulate a reply to see events here."
                : `No events in ${rangeLabel.toLowerCase()}.`}
            </div>
          ) : null}

          {activity.length > 0 && filtered.length === 0 ? (
            <div
              className="rounded-lg border border-dashed border-slate-200 bg-white p-6 text-center text-[13px] text-slate-500"
              data-testid="activity-filter-empty"
            >
              No events match the current filters.
            </div>
          ) : null}

          <div className="space-y-4">
            {grouped.map((group) => (
              <section key={group.key} data-testid={`activity-group-${group.key}`}>
                <h3 className="mb-2 px-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                  {group.key}
                </h3>
                <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200 bg-white text-[12px] overflow-hidden">
                  {group.events.map((event) => (
                    <ActivityEventRow key={event.id} event={event} />
                  ))}
                </ul>
              </section>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

CampaignActivityPanel.propTypes = {
  activity: PropTypes.arrayOf(PropTypes.object).isRequired,
  activityLoading: PropTypes.bool.isRequired,
  activityTotal: PropTypes.number,
  dateFilter: PropTypes.string.isRequired,
  onDateFilterChange: PropTypes.func.isRequired,
};
